import {
  useGetContienentsQuery,
  useGetCountriesBasedOnCountinentQuery,
} from '@/app/globalRedux/api/constantSlice'
import { usePageNumberUpdator } from '@/app/hooks/usePageParamUpdater'
import { useTranslation } from '@/app/i18n/client'
import { Country } from '@/app/types/companySearchType'
import { ContinentType } from '@/app/types/constantDataType'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment, TextField } from '@mui/material'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

const CompanyLocationFilter = ({
  searchTitle,
  locationText,
  continentSelectedList,
  countrySelectedList,
  setLocationText,
  setWholeLocation,
  addOrRemoveSelectedCountryList,
  addOrRemoveSelectedContinentList,
}: {
  searchTitle: string
  locationText: string
  continentSelectedList: ContinentType[]
  countrySelectedList: Country[]
  setLocationText: (value: string) => any
  setWholeLocation: (value: Country[]) => any
  addOrRemoveSelectedCountryList: (value: Country) => any
  addOrRemoveSelectedContinentList: (value: ContinentType) => any
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const goToPage = usePageNumberUpdator()

  const { data: continentList = [] } = useGetContienentsQuery<{
    data: ContinentType[]
  }>({})

  const {
    data: countriesList = [],
    isFetching,
    isError,
  } = useGetCountriesBasedOnCountinentQuery<{
    data: Country[]
    isFetching: boolean
    isError: boolean
  }>({
    continetIds: continentSelectedList?.map((item) => item.id),
  })

  const EXCLUDED_COUNTRIES: Record<string, string[]> = {
    'all countries': [
      'All countries',
      'Todos los paises',
      'Tous les pays',
      'Tutti i paesi',
    ],
  }

  const canShowOption = (countryName: string) => {
    const normalizedCountry = countryName.trim().toLowerCase()

    return !Object.values(EXCLUDED_COUNTRIES).some((excludedList) =>
      excludedList.some(
        (excluded) => excluded.toLowerCase() == normalizedCountry
      )
    )
  }

  useEffect(() => {
    let tempArray: Country[] = []

    countrySelectedList?.forEach((commonFilter) => {
      if (countriesList?.find((country) => country?.id == commonFilter?.id)) {
        tempArray = [...tempArray, commonFilter]
      }
    })
    if (tempArray && !isFetching && !isError) {
      dispatch(setWholeLocation(tempArray))
    }
  }, [countriesList, isFetching])

  return (
    <div className="shadow-[0_0_3px_0_rgba(0,0,0,0.06)] bg-neutral-50 border border-solid border-[#ebebeb] mt-[20px] rounded-[4px] mobile:mt-[10px]">
      <Accordion type="single" collapsible defaultValue="location">
        <AccordionItem value="location">
          <AccordionTrigger className="text-[18px] font-semibold text-[#222]">
            {t(searchTitle)}
          </AccordionTrigger>
          <AccordionContent className="!p-0 ">
            {/* {Continents} */}
            <Accordion
              type="single"
              collapsible
              className="!p-0"
              defaultValue="continents"
            >
              <AccordionItem value="continents" className="pb-[10px]">
                <AccordionTrigger className="text-[18px] font-semibold px-[0px]  ">
                  {t('Continent')}
                </AccordionTrigger>
                <AccordionContent className="px-[0px]">
                  <div className="flex flex-col  gap-[8px] ">
                    {continentList?.map((continent: ContinentType) => (
                      <div
                        className=" flex flex-row items-center gap-2  w-fit "
                        key={continent?.name}
                      >
                        <Checkbox
                          id={continent?.name}
                          className="border-2 border-input"
                          onClick={() => {
                            dispatch(
                              addOrRemoveSelectedContinentList(continent)
                            )
                            goToPage({ newPageNo: 1 })
                          }}
                          checked={
                            continentSelectedList?.find(
                              (item) => item?.id == continent?.id
                            )
                              ? true
                              : false
                          }
                        />
                        <Label
                          htmlFor={continent?.name}
                          className="  cursor-pointer text-sm  not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                        >
                          {continent?.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            {/* {Country} */}
            <Accordion type="single" collapsible defaultValue="country">
              <AccordionItem value="country" className="!p-[0px]">
                <AccordionTrigger className="text-[18px] font-semibold text-[#222] !p-[0]">
                  {t('Country')}
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col mt-[10px]">
                    <TextField
                      className=" mt-2"
                      placeholder={t('Search Country')}
                      value={locationText}
                      onChange={(event) => {
                        setLocationText(event?.target?.value)
                      }}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className="ml-[10px] "
                          >
                            <SearchIcon className="!w-[17.6px] !h-[17.6px]" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <div className="flex flex-col  gap-[8px] mt-[14px] max-h-[200px] overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                      {countriesList
                        ?.filter((item) =>
                          item.name
                            ?.toLowerCase()
                            .includes(locationText?.toLowerCase())
                        )
                        ?.map(
                          (country) =>
                            canShowOption(country?.name) && (
                              <div
                                className=" flex flex-row items-center gap-2 "
                                key={country.id}
                                onClick={() => {
                                  dispatch(
                                    addOrRemoveSelectedCountryList(country)
                                  )
                                  goToPage({ newPageNo: 1 })
                                }}
                              >
                                <Checkbox
                                  id={country.name}
                                  className="border-2 border-input"
                                  checked={
                                    countrySelectedList?.find(
                                      (item) => item?.id == country?.id
                                    )
                                      ? true
                                      : false
                                  }
                                />
                                <Label
                                  htmlFor={country.name}
                                  className="  cursor-pointer text-sm  not-italic leading-[normal] tracking-[normal] text-left text-[#6f6f6f]"
                                >
                                  {country?.name}
                                </Label>
                              </div>
                            )
                        )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

export default CompanyLocationFilter
